/*
  VBRNTT sign-ups (2026-10-28; pop-up and code 2026-11-02). Loaded on every
  page by snippets/vbrntt-signup-script.liquid. Three jobs:

  1. The sign-up pop-up (sections/vbrntt-signup-popup.liquid). Never on first
     paint: opens on whichever comes first of 15 seconds on the page, scrolling
     half-way, or exit intent (the pointer leaving through the top of the
     window). Once per visitor: opening it suppresses it for 30 days; signing
     up (here or on any other signup form) suppresses it for good. Never on
     the cart (the section isn't rendered there) or checkout (not themed).
     Closes on its X, Esc and a click outside it; focus is held inside while
     it's open (a modal <dialog>) and returns to where it was on close.
     In the theme editor it never opens by itself: it opens while its
     section is selected and closes when deselected.

     Storage: one localStorage key. Every read and write is wrapped; when
     storage throws or is empty the visitor is treated as new, so the worst
     case is the pop-up showing again on a later page, never an error.

     The form is Shopify's own customer form, posted normally: the page
     reloads to ?customer_posted=true#VbrnttPopupForm and the pop-up reopens
     at once in its success state (the code); an error comes back to
     /contact#VbrnttPopupForm and the pop-up reopens showing Shopify's message.

  2. Copy buttons on the sign-up code (snippets/vbrntt-signup-code.liquid).

  3. GHL (dormant while the webhook setting is empty). With the webhook set,
     every Shopify email signup form posts the address to it instead: the
     newsletter forms as { email, source: "website-newsletter", page }, the
     pop-up as { email, phone, sms_consent, source: "website-popup", page }
     (2026-11-02). The pop-up always sends both phone keys: phone is "" when
     blank or not a 10-digit US/Canada number (never blocks the signup), else
     +1XXXXXXXXXX; sms_consent is the tick box exactly, false unless ticked.
     The webhook URL is that one theme setting, read from the script tag
     (constraint 19). Never loses a signup: if the post fails, the form falls
     back to Shopify's normal submit (email only).
*/
(() => {
  const script = document.currentScript;
  const webhook = (script && script.dataset.webhook) || '';
  const success = (script && script.dataset.success) || '';

  const KEY = 'vbrntt-signup';
  const DAY = 24 * 60 * 60 * 1000;
  const read = () => {
    try {
      const v = JSON.parse(window.localStorage.getItem(KEY) || 'null');
      return v && typeof v === 'object' ? v : null;
    } catch (e) {
      return null;
    }
  };
  const write = (v) => {
    try {
      window.localStorage.setItem(KEY, JSON.stringify(v));
    } catch (e) {
      /* storage blocked or full: nothing remembered, the page still works */
    }
  };
  const isSignedUp = () => (read() || {}).state === 'signed-up';
  const suppressed = () => {
    const v = read();
    if (!v) return false;
    return v.state === 'signed-up' || (v.state === 'dismissed' && Number(v.until) > Date.now());
  };

  const designMode = !!(window.Shopify && window.Shopify.designMode);
  // Shopify's redirect after a customer form: ?customer_posted=true#<form id>.
  const posted = new URLSearchParams(window.location.search).get('customer_posted') === 'true';
  const postedForm = window.location.hash.slice(1);
  if (posted && !designMode) write({ state: 'signed-up' }); // any signup form: they've had the code

  // 2. Copy buttons: hidden until there's a clipboard to copy to.
  const wireCopy = (root) => {
    if (!navigator.clipboard) return;
    root.querySelectorAll('[data-vbrntt-copy]').forEach((button) => {
      if (button.dataset.vbrnttWired) return;
      button.dataset.vbrnttWired = 'true';
      button.hidden = false;
      button.addEventListener('click', () => {
        navigator.clipboard.writeText(button.dataset.vbrnttCopy).then(() => {
          button.textContent = 'Copied';
          window.setTimeout(() => (button.textContent = 'Copy'), 2000);
        }, () => {});
      });
    });
  };
  wireCopy(document);

  // 1. The pop-up.
  function initPopup(dialog, preview) {
    if (!dialog || typeof dialog.showModal !== 'function') return null;
    const ask = dialog.querySelector('[data-vbrntt-popup-ask]');
    const done = dialog.querySelector('[data-vbrntt-popup-done]');
    const doneHeading = dialog.querySelector('[data-vbrntt-popup-done-heading]');
    let returnTo = null;

    const open = () => {
      if (dialog.open) return;
      returnTo = document.activeElement;
      dialog.showModal();
      if (!preview && !isSignedUp()) write({ state: 'dismissed', until: Date.now() + 30 * DAY });
      const target = done && !done.hidden ? doneHeading : dialog.querySelector('input[type="email"]');
      (target || dialog).focus();
    };
    const close = () => {
      if (dialog.open) dialog.close();
    };
    dialog.addEventListener('close', () => {
      if (returnTo && typeof returnTo.focus === 'function' && document.contains(returnTo)) returnTo.focus();
      returnTo = null;
    });
    // Esc fires the dialog's cancel event: closed the same way as the X.
    dialog.addEventListener('cancel', (event) => {
      event.preventDefault();
      close();
    });
    dialog.querySelectorAll('[data-vbrntt-popup-close]').forEach((button) => button.addEventListener('click', close));
    // A click outside the panel lands on the <dialog> itself (its backdrop).
    dialog.addEventListener('click', (event) => {
      if (event.target === dialog) close();
    });
    const showDone = () => {
      if (ask) ask.hidden = true;
      if (done) done.hidden = false;
      if (doneHeading) doneHeading.focus();
    };
    return { dialog, open, close, showDone };
  }

  // Opens on the first of 15 seconds, half-way down, or the pointer leaving through the top.
  function arm(popup) {
    let fired = false;
    let timer;
    const onScroll = () => {
      const max = document.documentElement.scrollHeight - window.innerHeight;
      if (max > 0 && window.scrollY / max >= 0.5) fire();
    };
    const onLeave = (event) => {
      if (!event.relatedTarget && event.clientY <= 0) fire();
    };
    function fire() {
      if (fired) return;
      fired = true;
      window.clearTimeout(timer);
      window.removeEventListener('scroll', onScroll);
      document.removeEventListener('mouseout', onLeave);
      if (!suppressed()) popup.open(); // checked again: another tab may have signed up meanwhile
    }
    timer = window.setTimeout(fire, 15000);
    window.addEventListener('scroll', onScroll, { passive: true });
    document.addEventListener('mouseout', onLeave);
  }

  const dialog = document.querySelector('[data-vbrntt-popup]');
  let popup = initPopup(dialog, designMode);
  if (popup && !designMode) {
    const ours = postedForm === dialog.dataset.formId;
    if (ours && (posted || dialog.querySelector('[data-vbrntt-popup-error]'))) popup.open();
    else if (!suppressed()) arm(popup);
  }

  // Theme editor: open while the section is selected; close when deselected.
  if (designMode) {
    const mine = (event) => {
      const el = document.querySelector('[data-vbrntt-popup]');
      return el && event.detail && el.dataset.sectionId === event.detail.sectionId ? el : null;
    };
    document.addEventListener('shopify:section:select', (event) => {
      const el = mine(event);
      if (!el) return;
      if (!popup || popup.dialog !== el) popup = initPopup(el, true);
      if (popup) popup.open();
    });
    document.addEventListener('shopify:section:deselect', (event) => {
      if (popup && mine(event)) popup.close();
    });
    // A setting changed: the section is re-rendered while still selected.
    document.addEventListener('shopify:section:load', (event) => {
      const el = mine(event);
      if (!el) return;
      popup = initPopup(el, true);
      wireCopy(el);
      if (popup) popup.open();
    });
  }

  // 3. GHL webhook (dormant while the setting is empty).
  if (!webhook) return;
  // US/Canada: 10 digits, or 11 starting with 1, to +1XXXXXXXXXX. Anything else (blank, too short, foreign) is "":
  // the email signup still goes through.
  const normalisePhone = (raw) => {
    const digits = String(raw || '').replace(/\D/g, '');
    if (digits.length === 10) return `+1${digits}`;
    if (digits.length === 11 && digits[0] === '1') return `+${digits}`;
    return '';
  };
  const codeTemplate = document.getElementById('VbrnttSignupCode');
  const showSuccess = (form) => {
    const message = document.createElement('p');
    message.className = 'newsletter-form__message newsletter-form__message--success form__message';
    message.setAttribute('role', 'status');
    message.textContent = success;
    form.replaceChildren(message, ...(codeTemplate ? [codeTemplate.content.cloneNode(true)] : []));
    wireCopy(form);
  };
  // Honeypot (2026-11-02): every webhook post costs Kendall a GHL run. The pop-up has its field in the markup; the
  // other signup forms get the same one here. Hidden from people and screen readers; filled = a bot, which sees the
  // success state while nothing is posted (not to GHL, not to Shopify). It stops form-filling bots only: anyone
  // posting to the webhook URL directly bypasses every form (see the brief's hardening step).
  const honeypotFor = (form) => {
    let field = form.querySelector('[data-vbrntt-honeypot]');
    if (!field) {
      const wrap = document.createElement('div');
      wrap.className = 'vbrntt-hp';
      wrap.setAttribute('aria-hidden', 'true');
      wrap.style.cssText = 'position:absolute;left:-10000px;width:1px;height:1px;overflow:hidden'; // the password layout has no overrides CSS
      field = document.createElement('input');
      Object.assign(field, { type: 'text', name: 'vbrntt_company', tabIndex: -1, autocomplete: 'off' });
      field.setAttribute('data-vbrntt-honeypot', '');
      wrap.append(field);
      form.append(wrap);
    }
    return field;
  };
  document.querySelectorAll('form.newsletter-form').forEach((form) => {
    const honeypot = honeypotFor(form);
    form.addEventListener('submit', async (event) => {
      if (form.dataset.vbrnttFallback === 'true') return;
      const input = form.querySelector('input[type="email"]');
      if (!input || !input.value || !input.checkValidity()) return;
      event.preventDefault();
      const inPopup = !!(popup && popup.dialog.contains(form));
      if (honeypot.value) {
        if (inPopup) popup.showDone();
        else showSuccess(form);
        return;
      }
      const button = form.querySelector('button[type="submit"], button[name="commit"]');
      if (button) button.disabled = true;
      try {
        const response = await fetch(webhook, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(
            inPopup
              ? {
                  email: input.value.trim(),
                  phone: normalisePhone(form.querySelector('[data-vbrntt-phone]')?.value),
                  sms_consent: !!form.querySelector('[data-vbrntt-sms-consent]')?.checked,
                  source: 'website-popup',
                  page: window.location.pathname,
                }
              : { email: input.value.trim(), source: 'website-newsletter', page: window.location.pathname }
          ),
        });
        if (!response.ok) throw new Error(`webhook ${response.status}`);
        write({ state: 'signed-up' });
        if (inPopup) {
          popup.showDone();
          return;
        }
        showSuccess(form);
      } catch (error) {
        form.dataset.vbrnttFallback = 'true';
        if (button) button.disabled = false;
        form.requestSubmit ? form.requestSubmit() : form.submit();
      }
    });
  });
})();
